// /coupons — panel de cupones de días gratis (danger_zone).
//
// Dos modos de creación: cupón único y lote (hasta 500). Al crear, los códigos
// se muestran en pantalla y se pueden bajar en PDF (client-side, mismo patrón
// que el reporte financiero). Los códigos siguen visibles en el listado —
// el PDF es para imprimir/repartir, no la única copia.

import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCoupons,
  listCoupons,
  revokeCoupon,
  listPartners,
  listPartnerPlans,
  AdminApiError,
  type AdminCoupon,
  type CouponBatchResult,
  type CouponStatus,
} from '@/lib/api';
import { downloadCouponsPdf } from '@/lib/couponsPdf';
import { useSession } from '@/hooks/useSession';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls } from '@/lib/form-styles';

const STATUS_BADGE: Record<CouponStatus, { label: string; cls: string }> = {
  issued: { label: 'sin usar', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  redeemed: { label: 'canjeado', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  revoked: { label: 'anulado', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

export default function Coupons(): JSX.Element {
  const session = useSession();
  const queryClient = useQueryClient();

  // --- Form de creación ---
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [partnerId, setPartnerId] = useState('');
  const [planId, setPlanId] = useState('');
  const [freeDays, setFreeDays] = useState('30');
  const [quantity, setQuantity] = useState('10');
  const [note, setNote] = useState('');
  const [formErr, setFormErr] = useState<string | null>(null);
  const [created, setCreated] = useState<CouponBatchResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  // --- Filtros del listado ---
  const [fltPartner, setFltPartner] = useState('');
  const [fltStatus, setFltStatus] = useState('');
  const [fltCode, setFltCode] = useState('');

  const [toRevoke, setToRevoke] = useState<AdminCoupon | null>(null);

  const partnersQ = useQuery({ queryKey: ['admin-partners'], queryFn: listPartners });
  const plansQ = useQuery({
    queryKey: ['admin-partner-plans', partnerId],
    queryFn: () => listPartnerPlans(partnerId),
    enabled: partnerId !== '',
  });

  const listQ = useQuery({
    queryKey: ['admin-coupons', fltPartner, fltStatus, fltCode],
    queryFn: () =>
      listCoupons({
        ...(fltPartner ? { partner_id: fltPartner } : {}),
        ...(fltStatus ? { status: fltStatus as CouponStatus } : {}),
        ...(fltCode.trim() ? { code: fltCode.trim().toUpperCase() } : {}),
        limit: 200,
      }),
  });

  const createM = useMutation({
    mutationFn: createCoupons,
    onSuccess: (res) => {
      setCreated(res);
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
    },
    onError: (e) => {
      setFormErr(e instanceof AdminApiError ? e.message : 'No se pudieron crear los cupones.');
    },
  });

  const revokeM = useMutation({
    mutationFn: (couponId: string) => revokeCoupon(couponId),
    onSuccess: () => {
      setToRevoke(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
    },
  });

  // Los planes gratis no admiten cupón (el backend también lo rechaza).
  const eligiblePlans = (plansQ.data ?? []).filter(
    (p) => p.status === 'active' && p.price_amount > 0,
  );

  function onCreate(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormErr(null);
    const days = parseInt(freeDays, 10);
    const qty = mode === 'single' ? 1 : parseInt(quantity, 10);
    if (!partnerId || !planId) {
      setFormErr('Elegí partner y plan.');
      return;
    }
    if (!Number.isFinite(days) || days < 1 || days > 3650) {
      setFormErr('Los días gratis deben estar entre 1 y 3650.');
      return;
    }
    if (!Number.isFinite(qty) || qty < 1 || qty > 500) {
      setFormErr('La cantidad debe estar entre 1 y 500.');
      return;
    }
    createM.mutate({
      partner_id: partnerId,
      plan_id: planId,
      free_days: days,
      quantity: qty,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  }

  async function onDownloadPdf(): Promise<void> {
    if (!created) return;
    setDownloading(true);
    try {
      const partnerLabel =
        partnersQ.data?.find((p) => p.partner_id === created.partner_id)?.display_name ??
        created.partner_id;
      await downloadCouponsPdf({
        batch: created,
        partnerLabel,
        generatedBy: session?.display_name ?? 'admin',
      });
    } finally {
      setDownloading(false);
    }
  }

  async function onCopyAll(): Promise<void> {
    if (!created) return;
    await navigator.clipboard.writeText(created.coupons.map((c) => c.code).join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 data-help-page="coupons.page" data-help="coupons.page" className="text-xl font-semibold text-ink dark:text-ink-inverted">Cupones</h1>
        <p className="text-sm text-ink-muted">
          Códigos de un solo uso que activan un plan por una cantidad de días, gratis. Manejalos
          como plata: cualquiera que vea un código sin usar puede canjearlo.
        </p>
      </div>

      {/* --- Creación --- */}
      <section
        data-help="coupons.new"
        className="rounded-lg border border-surface-border p-4 dark:border-surface-dark-border"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink dark:text-ink-inverted">Crear cupones</h2>
          <div className="flex rounded-lg border border-surface-border text-sm dark:border-surface-dark-border">
            {(['single', 'batch'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`px-3 py-1.5 first:rounded-l-lg last:rounded-r-lg ${
                  mode === m
                    ? 'bg-brand text-white'
                    : 'text-ink-muted hover:text-ink dark:hover:text-ink-inverted'
                }`}
              >
                {m === 'single' ? 'Cupón único' : 'Lote'}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={onCreate} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Partner">
            <select
              value={partnerId}
              onChange={(e) => {
                setPartnerId(e.target.value);
                setPlanId('');
              }}
              className={fieldCls()}
            >
              <option value="">Elegir…</option>
              {partnersQ.data?.map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Plan">
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className={fieldCls()}
              disabled={!partnerId}
            >
              <option value="">{partnerId ? 'Elegir…' : 'Primero el partner'}</option>
              {eligiblePlans.map((p) => (
                <option key={p.plan_id} value={p.plan_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Días gratis">
            <input
              type="number"
              min={1}
              max={3650}
              value={freeDays}
              onChange={(e) => setFreeDays(e.target.value)}
              className={fieldCls()}
            />
          </Field>
          {mode === 'batch' ? (
            <Field label="Cantidad">
              <input
                type="number"
                min={1}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className={fieldCls()}
              />
            </Field>
          ) : null}
          <Field label="Nota (interna, opcional)">
            <input
              type="text"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ej: campaña lanzamiento julio"
              className={fieldCls()}
            />
          </Field>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={createM.isPending}
              className="min-h-[2.5rem] w-full rounded-lg bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
            >
              {createM.isPending
                ? 'Generando…'
                : mode === 'single'
                  ? 'Crear cupón'
                  : 'Crear lote'}
            </button>
          </div>
        </form>
        {formErr ? <p className="mt-2 text-sm text-red-600">{formErr}</p> : null}

        {created ? (
          <div className="mt-4 rounded-lg border border-brand bg-brand/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-ink dark:text-ink-inverted">
                  {created.coupons.length === 1
                    ? 'Cupón creado'
                    : `${created.coupons.length} cupones creados`}{' '}
                  · {created.plan_name} · {created.free_days} días gratis
                </p>
                <p className="text-xs text-ink-muted">
                  Guardá el PDF si los vas a imprimir. Los códigos quedan también en el listado de
                  abajo.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void onCopyAll()}
                  className="min-h-[2.25rem] rounded-lg border border-surface-border px-3 text-sm text-ink hover:bg-surface-border/30 dark:border-surface-dark-border dark:text-ink-inverted"
                >
                  {copied ? 'Copiado ✓' : 'Copiar todos'}
                </button>
                <button
                  type="button"
                  onClick={() => void onDownloadPdf()}
                  disabled={downloading}
                  className="min-h-[2.25rem] rounded-lg bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
                >
                  {downloading ? 'Generando…' : 'Descargar PDF'}
                </button>
                <button
                  type="button"
                  onClick={() => setCreated(null)}
                  aria-label="Cerrar"
                  className="min-h-[2.25rem] rounded-lg px-2 text-sm text-ink-muted hover:text-ink dark:hover:text-ink-inverted"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              {created.coupons.map((c) => (
                <code
                  key={c.coupon_id}
                  className="rounded border border-surface-border bg-surface px-2 py-1.5 text-center font-mono text-sm tracking-widest text-ink dark:border-surface-dark-border dark:bg-surface-dark dark:text-ink-inverted"
                >
                  {c.code}
                </code>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {/* --- Listado --- */}
      <section className="rounded-lg border border-surface-border dark:border-surface-dark-border">
        <div className="flex flex-wrap gap-3 border-b border-surface-border p-3 dark:border-surface-dark-border">
          <Field label="Partner">
            <select value={fltPartner} onChange={(e) => setFltPartner(e.target.value)} className={fieldCls()}>
              <option value="">Todos</option>
              {partnersQ.data?.map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estado">
            <select value={fltStatus} onChange={(e) => setFltStatus(e.target.value)} className={fieldCls()}>
              <option value="">Todos</option>
              <option value="issued">Sin usar</option>
              <option value="redeemed">Canjeados</option>
              <option value="revoked">Anulados</option>
            </select>
          </Field>
          <Field label="Código exacto">
            <input
              type="text"
              value={fltCode}
              onChange={(e) => setFltCode(e.target.value)}
              placeholder="XXXXXXXX"
              className={fieldCls({ mono: true, extra: 'uppercase' })}
            />
          </Field>
        </div>

        {listQ.isLoading ? <p className="p-4 text-sm text-ink-muted">Cargando…</p> : null}
        {listQ.isError ? (
          <p className="p-4 text-sm text-red-600">
            {listQ.error instanceof AdminApiError ? listQ.error.message : 'No se pudo cargar.'}
          </p>
        ) : null}

        {listQ.data ? (
          <div className="overflow-x-auto">
            <table data-help="coupons.list" className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted dark:border-surface-dark-border">
                  <th className="p-2">Código</th>
                  <th className="p-2">Plan</th>
                  <th className="p-2 text-right">Días</th>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Creado</th>
                  <th className="p-2">Canjeado por</th>
                  <th className="p-2">Nota</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {listQ.data.coupons.map((c) => (
                  <tr
                    key={c.coupon_id}
                    className="border-b border-surface-border/50 dark:border-surface-dark-border/50"
                  >
                    <td className="p-2 font-mono tracking-widest text-ink dark:text-ink-inverted">
                      {c.code}
                    </td>
                    <td className="p-2">{c.plan_name ?? c.plan_id}</td>
                    <td className="p-2 text-right">{c.free_days}</td>
                    <td className="p-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE[c.status].cls}`}>
                        {STATUS_BADGE[c.status].label}
                      </span>
                    </td>
                    <td className="p-2 text-ink-muted">{c.created_at.slice(0, 10)}</td>
                    <td className="p-2">
                      {c.redeemed_by_user_id ? (
                        <Link
                          to={`/users/${c.redeemed_by_user_id}?partner_id=${encodeURIComponent(c.partner_id)}`}
                          className="text-brand underline hover:opacity-80"
                        >
                          {c.redeemed_by_display_name ?? c.redeemed_by_user_id.slice(0, 8)}
                        </Link>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="max-w-[12rem] truncate p-2 text-ink-muted" title={c.note ?? ''}>
                      {c.note ?? '—'}
                    </td>
                    <td className="p-2 text-right">
                      {c.status === 'issued' ? (
                        <button
                          type="button"
                          onClick={() => setToRevoke(c)}
                          className="text-xs text-red-600 underline hover:opacity-80"
                        >
                          Anular
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {listQ.data.coupons.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-ink-muted">
                      Sin cupones con esos filtros.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            <p className="p-2 text-xs text-ink-muted">
              {listQ.data.total} en total{listQ.data.total > listQ.data.coupons.length ? ` (mostrando ${listQ.data.coupons.length})` : ''}
            </p>
          </div>
        ) : null}
      </section>

      <ConfirmDialog
        open={toRevoke !== null}
        title="Anular cupón"
        description={
          <>
            El cupón <code className="font-mono">{toRevoke?.code}</code> deja de ser canjeable, para
            siempre. No afecta cupones ya canjeados ni suscripciones activas.
          </>
        }
        confirmLabel="Anular"
        danger
        loading={revokeM.isPending}
        onConfirm={() => {
          if (toRevoke) revokeM.mutate(toRevoke.coupon_id);
        }}
        onCancel={() => setToRevoke(null)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  );
}
